import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { Calendar, CheckCircle, Search, ExternalLink } from 'lucide-react';

const SeoHealthLogsTable = ({ domainId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedLog, setExpandedLog] = useState(null);

    useEffect(() => {
        const fetchLogs = async () => {
            if (!domainId) return;
            setLoading(true);
            try {
                const data = await apiService.makeRequest(`/organic-seo/maintenance/logs?domain_id=${domainId}`);
                setLogs(data);
            } catch (error) {
                console.error("Error fetching SEO maintenance logs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [domainId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        const d = new Date(dateString);
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' });
    };

    if (loading) return <div className="p-4 text-center text-gray-500">Loading Maintenance Reports...</div>;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold flex items-center gap-2 mb-6 text-gray-800">
                <Calendar className="text-purple-600" />
                Nightly Maintenance Logs
            </h2>

            <div className="overflow-x-auto text-sm">
                <table className="w-full text-left font-mono">
                    <thead className="text-gray-500 border-b border-gray-200 bg-gray-50">
                        <tr>
                            <th className="py-3 px-2">Date (UTC)</th>
                            <th className="py-3 px-2">Domain</th>
                            <th className="py-3 px-2">Operation</th>
                            <th className="py-3 px-2">Scanned</th>
                            <th className="py-3 px-2">Fixed</th>
                            <th className="py-3 px-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700">
                        {logs.map(log => (
                            <React.Fragment key={log.id}>
                                <tr className="hover:bg-gray-50 transition-colors">
                                    <td className="py-4 px-2">{formatDate(log.run_date)}</td>
                                    <td className="py-4 px-2 truncate max-w-[150px]" title={log.domain_url}>{log.domain_url.replace('https://', '').replace('www.', '')}</td>
                                    <td className="py-4 px-2 capitalize font-medium text-gray-900">{log.job_type.replace('_', ' ')}</td>
                                    <td className="py-4 px-2">{log.items_scanned} pages</td>
                                    <td className="py-4 px-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${log.items_fixed > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {log.items_fixed} fixed
                                        </span>
                                    </td>
                                    <td className="py-4 px-2 text-right">
                                        <button 
                                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)} 
                                            className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 ml-auto"
                                        >
                                            <Search className="w-4 h-4" /> View Details
                                        </button>
                                    </td>
                                </tr>
                                
                                {expandedLog === log.id && (
                                    <tr className="bg-purple-50">
                                        <td colSpan="6" className="p-4 border-l-4 border-purple-500">
                                            <h4 className="font-bold text-gray-900 mb-3">Detailed Operations Report</h4>
                                            
                                            {log.detailed_report && log.detailed_report.length > 0 ? (
                                                <ul className="space-y-3">
                                                    {log.detailed_report.map((detail, idx) => (
                                                        <li key={idx} className="bg-white p-3 rounded-lg border border-purple-100 shadow-sm">
                                                            {detail.post_id && (
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <div className="font-semibold text-gray-800">Post ID: {detail.post_id}</div>
                                                                    <a href={`${log.domain_url}/wp-admin/post.php?post=${detail.post_id}&action=edit`} target="_blank" rel="noreferrer" className="text-xs text-purple-600 flex items-center gap-1 hover:underline">
                                                                        <ExternalLink className="w-3 h-3" /> WP Editor
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {detail.title && <div className="text-sm text-gray-600 mb-2 truncate font-sans" title={detail.title}><i>"{detail.title}"</i></div>}
                                                            
                                                            {detail.fixed_links && (
                                                                <div className="mt-2 text-xs">
                                                                    <span className="text-green-600 font-bold mb-1 block">✓ Removed Broken URLs:</span>
                                                                    <ul className="list-disc pl-5 text-gray-600 space-y-1 font-mono">
                                                                        {detail.fixed_links.map((url, i) => (
                                                                            <li key={i} className="break-all">{url}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            
                                                            {detail.info && <div className="text-blue-600 font-mono text-xs mt-2">{detail.info}</div>}
                                                            {detail.error && <div className="text-red-500 font-mono text-xs mt-2">Error: {detail.error}</div>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="text-gray-500 italic flex items-center gap-2 p-3">
                                                    <CheckCircle className="w-5 h-5 text-green-500" /> No actionable defects found during this run.
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {logs.length === 0 && (
                            <tr>
                                <td colSpan="6" className="py-6 text-center text-gray-500">No maintenance logs found yet. Wait for the nightly cron job, or trigger manually.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SeoHealthLogsTable;
